/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-restricted-imports */
import { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { useQuery } from 'hooks/useQuery';
import { budgetService } from 'services/budget';
import { serviceOrderService } from 'services/serviceOrder';

import {
  calculateGatewayFee,
  calculateValueWithFee,
  getAvailableGateways,
} from '../constants/gatewayConfig';

const BudgetContext = createContext();

export function BudgetProvider({ children }) {
  const [currentView, setCurrentView] = useState('pending');

  const [selectedBudget, setSelectedBudget] = useState(null);

  const [budgetFilters, setBudgetFilters] = useState({
    search: '',
    process: '',
    productType: '',
    productModel: '',
    status: '',
    dateStart: '',
    dateEnd: '',
    page: 1,
    rowsPerPage: 5,
  });

  const [budget, setBudget] = useState();
  const [loadingBudgetData, setLoadingBudgetData] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [generatingPayment, setGeneratingPayment] = useState(false);
  const [confirmingBudget, setConfirmingBudget] = useState(false);
  const [paymentLink, setPaymentLink] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [paymentLinkValue, setPaymentLinkValue] = useState(0);
  const [finalBudgetValue, setFinalBudgetValue] = useState(0);

  const gateways = getAvailableGateways();

  const [budgetsData, , loadingBudgets, refetchBudgets] = useQuery(
    () => budgetService.getPendingBudget(budgetFilters),
    [budgetFilters],
  );

  const budgets = budgetsData?.response || [];
  const total = budgetsData?.total || 0;
  const totalPages = budgetsData?.totalPages || 0;

  const updateBudgetFilters = (newFilters) => {
    setBudgetFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const handleSelectBudget = async (selectedOS) => {
    setSelectedBudget(selectedOS);
    setCurrentView('budget');
    setLoadingBudgetData(true);

    setBudget({
      ...selectedOS,
      additionalServices: [],
      components: [],
      status: selectedOS.budget?.status || 1,
      initialStatusId: selectedOS.budget?.status || 1,
      rejectionReasonId: null,
    });

    try {
      const [servicesResponse, partsResponse] = await Promise.all([
        budgetService.getBudgetAdditionalServices({
          productType: selectedOS.productId,
          budgetId: selectedOS.budget?.id,
        }),
        budgetService.getBudgetParts({
          budgetId: selectedOS.budget?.id,
        }),
      ]);

      const additionalServices = servicesResponse?.data || [];
      const components = partsResponse?.data || [];

      setBudget((prev) => ({
        ...prev,
        additionalServices,
        components,
      }));
    } catch (error) {
      console.error('Erro ao buscar dados do orçamento:', error);
    } finally {
      setLoadingBudgetData(false);
    }
  };

  const handleBackToPending = () => {
    setCurrentView('pending');
    setSelectedBudget(null);
  };

  const updateComponent = (componentId, amount) => {
    setBudget((prev) => ({
      ...prev,
      components: prev.components.map((component) =>
        component.id == componentId
          ? {
              ...component,
              amount,
              status: amount > 0 ? 'selecionado' : '',
            }
          : component,
      ),
    }));
  };

  const updateComponentPrice = (componentId, sellPrice) => {
    setBudget((prev) => ({
      ...prev,
      components: prev.components.map((component) =>
        component.id === componentId ? { ...component, sellPrice } : component,
      ),
    }));
  };

  const updateService = (servicoId, selected) => {
    setBudget((prev) => ({
      ...prev,
      additionalServices: prev.additionalServices.map((servico) =>
        servico.id === servicoId ? { ...servico, selected } : servico,
      ),
    }));
  };

  const updateServicePrice = (servicoId, value) => {
    setBudget((prev) => ({
      ...prev,
      additionalServices: prev.additionalServices.map((servico) =>
        servico.id === servicoId ? { ...servico, value } : servico,
      ),
    }));
  };

  const updatePaymentType = (paymentType) => {
    setBudget((prev) => ({ ...prev, paymentType }));
  };

  const updateGateway = (gateway) => {
    setBudget((prev) => ({ ...prev, gateway }));
  };

  const updateStatus = (status) => {
    setBudget((prev) => ({
      ...prev,
      status,
      rejectionReasonId: status === 3 || status === 7 ? prev.rejectionReasonId : null,
    }));
  };

  const updateRejectionReason = (rejectionReasonId) => {
    setBudget((prev) => ({ ...prev, rejectionReasonId }));
  };

  const updateDiscount = (discount) => {
    setBudget((prev) => ({ ...prev, discount }));
  };

  const updateInstallments = (installments) => {
    setBudget((prev) => {
      const newPaymentType = installments > 1 ? 'cartao' : prev.paymentType;

      return { ...prev, installments, paymentType: newPaymentType };
    });
  };

  const calculateValueWithCardFee = (originalValue) => {
    const { gateway, paymentType, installments = 1 } = budget || {};

    if (paymentType !== 'cartao' || !gateway) {
      return originalValue;
    }

    const feePercentage = calculateGatewayFee(gateway, paymentType, installments);
    return calculateValueWithFee(originalValue, feePercentage);
  };

  // Atualizar automaticamente o valor do link quando o orçamento muda
  useEffect(() => {
    if (!budget) return;

    const totalComponents = (budget.components || []).reduce(
      (acc, comp) => acc + comp.sellPrice * comp.amount,
      0,
    );
    const totalServices = (budget.additionalServices || []).reduce(
      (acc, service) => acc + (service.selected ? service.value : 0),
      0,
    );
    const discountAmount = (totalComponents * (budget.discount || 0)) / 100;
    const subtotalValue = totalComponents + totalServices - discountAmount;

    const isCard = budget.paymentType === 'cartao';
    const totalValue = isCard ? calculateValueWithCardFee(subtotalValue) : subtotalValue;

    setPaymentLinkValue(totalValue);
    setFinalBudgetValue(totalValue);
  }, [
    budget?.components,
    budget?.additionalServices,
    budget?.discount,
    budget?.paymentType,
    budget?.gateway,
    budget?.installments,
  ]);

  const validateBudget = () => {
    const errors = [];

    if ((budget.status === 3 || budget.status === 7) && !budget.rejectionReasonId) {
      errors.push('Motivo de recusa é obrigatório para o status selecionado');
    }

    if (budget.status === 60 && (!finalBudgetValue || finalBudgetValue <= 0)) {
      errors.push('Valor final do orçamento é obrigatório para o status "Pagamento Realizado SC+"');
    }

    const componentsWithZeroPrice = (budget.components || [])
      .filter((comp) => comp.amount > 0 && (!comp.sellPrice || comp.sellPrice === 0))
      .map((comp) => comp.name || comp.description || 'Componente sem nome');

    if (componentsWithZeroPrice.length > 0) {
      errors.push(
        `As seguintes peças estão com valor R$ 0,00: ${componentsWithZeroPrice.join(', ')}`,
      );
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const confirmBudget = async () => {
    if (!budget) {
      toast.warning('Nenhum orçamento selecionado!');
      return;
    }

    if (!budget.budget?.id) {
      toast.warning('ID do orçamento não encontrado!');
      return;
    }

    if (!validateBudget()) {
      validationErrors.forEach((error) => toast.error(error));
      return;
    }

    setConfirmingBudget(true);

    try {
      const totalComponents = (budget.components || []).reduce(
        (acc, comp) => acc + comp.sellPrice * comp.amount,
        0,
      );
      const totalServices = (budget.additionalServices || []).reduce(
        (acc, service) => acc + (service.selected ? service.value : 0),
        0,
      );
      const discountAmount = (totalComponents * (budget.discount || 0)) / 100;
      const subtotalValue = totalComponents + totalServices - discountAmount;

      const isCard = budget.paymentType === 'cartao';
      const totalValue = isCard ? calculateValueWithCardFee(subtotalValue) : subtotalValue;

      const selectedComponents = (budget.components || [])
        .filter((comp) => comp.amount > 0)
        .map((comp) => ({
          id: comp.id,
          itemId: comp.itemId || 0,
          partNumber: comp.partNumber || '',
          sellPrice: comp.sellPrice || 0,
          costPrice: comp.costPrice || 0,
          amount: comp.amount || 0,
        }));

      const selectedServices = (budget.additionalServices || [])
        .filter((service) => service.selected)
        .map((service) => ({
          id: service.id,
          value: service.value || 0,
        }));

      // Se status for 60 (Pagamento Realizado SC+), usar o valor final definido pelo usuário
      const finalValue = budget.status === 60 ? finalBudgetValue : totalValue;

      const payload = {
        serviceOrder: budget.serviceOrder || '',
        jobOrder: budget.jobOrder || 0,
        components: selectedComponents,
        additionalServices: selectedServices,
        budget: {
          id: budget.budget.id,
          statusId: budget.status || 1,
          previousStatusId: budget.initialStatusId || 1,
          rejectionReasonId: budget.rejectionReasonId || undefined,
          totalValue: finalValue,
          previousTotalValue: budget.budget.totalValue || 0,
          discount: budget.discount || 0,
          additionalValue: totalServices,
          installments: budget.installments,
          gateway: budget.gateway,
          paymentType: budget.paymentType,
        },
      };

      await budgetService.saveBudget(payload);

      setBudget((prev) => ({
        ...prev,
        budget: {
          ...prev.budget,
          statusId: prev.status,
          totalValue: totalValue,
        },
      }));

      updateBudgetFilters({
        search: '',
        process: '',
        productType: '',
        productModel: '',
        status: '',
        dateStart: '',
        dateEnd: '',
        page: 1,
      });

      setCurrentView('pending');

      window.scrollTo({ top: 0, behavior: 'smooth' });

      toast.success('Orçamento confirmado com sucesso!');
    } catch (error) {
      console.error('Erro ao confirmar orçamento:', error);
      toast.error(
        error?.response?.data?.message || 'Erro ao confirmar orçamento. Tente novamente.',
      );
    } finally {
      setConfirmingBudget(false);
    }
  };

  const generateWhatsAppMessage = () => {
    const totalComponents = (budget.components || []).reduce(
      (acc, comp) => acc + comp.sellPrice * comp.amount,
      0,
    );
    const totalServices = (budget.additionalServices || []).reduce(
      (acc, service) => acc + (service.selected ? service.value : 0),
      0,
    );
    const discountAmount = (totalComponents * (budget.discount || 0)) / 100;
    const subtotalValue = totalComponents + totalServices - discountAmount;

    const isCard = budget.paymentType === 'cartao';
    const totalValue = isCard ? calculateValueWithCardFee(subtotalValue) : subtotalValue;

    const formatMoney = (value) =>
      new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

    const getFirstName = () => {
      try {
        const userStorage = localStorage.getItem('@Gaia:user');
        if (userStorage) {
          const user = JSON.parse(userStorage);
          const fullName = user?.name || '';
          return fullName.split(' ')[0] || 'Técnico';
        }
      } catch (error) {
        console.error('Erro ao buscar usuário do localStorage:', error);
      }
      return 'Técnico';
    };

    const getProductsList = () => {
      const selectedComponents = (budget.components || []).filter((comp) => comp.amount > 0);

      const items = [];

      selectedComponents.forEach((comp) => {
        items.push(`•    ${comp.name || comp.description}`);
      });

      return items.length > 0 ? items.join('\n') : '•    Componentes diversos';
    };

    const clientName = budget.client?.name || 'Cliente';
    const firstName = getFirstName();
    const serviceOrder = budget.serviceOrder || 'N/A';
    const productsList = getProductsList();

    let message = `Olá, ${clientName}, como você está 😀?\n\n`;
    message += `Eu sou a ${firstName}, da Autorizada Samsung ICF Comércio Parq. Tecnológico, `;
    message += `e estou aqui para cuidar do seu atendimento.\n\n`;
    message += `Finalizamos a análise técnica do seu equipamento (Ordem de Serviço ${serviceOrder}) `;
    message += `e Identificamos a necessidade de reparo nas seguintes áreas, para garantir o pleno `;
    message += `funcionamento do seu produto:\n\n`;
    message += `${productsList}\n\n`;
    message += `Nosso orçamento, que já inclui peças originais e a garantia do serviço de uma `;
    message += `autorizada Samsung, fica em ${formatMoney(totalValue)}.\n\n`;
    message += `Para sua comodidade, oferecemos duas formas de pagamento:\n`;
    message += `•    À vista via PIX (Chave: 38061980000539)\n`;
    message += `•    Parcelamento flexível em até 6x sem juros no cartão.\n\n`;
    message += `Assim que você nos sinalizar qual opção prefere, daremos andamento imediato ao `;
    message += `reparo. Garantimos a você um serviço de excelência❤️.\n\n`;
    message += `Estou à disposição para qualquer dúvida e fico no aguardo da sua confirmação para `;
    message += `prosseguirmos🙏🏻!`;

    return message;
  };

  const sendWhatsAppMessage = () => {
    const message = generateWhatsAppMessage();
    const phoneNumber = budget.client?.phoneNumber?.replace(/\D/g, '') || '';

    if (!phoneNumber) {
      toast.success('Número de telefone não encontrado!');
      return;
    }

    const whatsappUrl = `https://wa.me/55${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const copyWhatsAppMessage = () => {
    const message = generateWhatsAppMessage();

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(message)
        .then(() => {
          toast.success('Mensagem copiada para a área de transferência!');
        })
        .catch(() => {
          fallbackCopyTextToClipboard(message);
        });
    } else {
      fallbackCopyTextToClipboard(message);
    }
  };

  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      toast.success('Mensagem copiada para a área de transferência!');
    } catch (err) {
      toast.error('Erro ao copiar mensagem. Tente novamente.');
    }

    document.body.removeChild(textArea);
  };

  const downloadServiceOrderReport = async () => {
    const serviceOrderNumber = budget?.jobOrder;

    if (!serviceOrderNumber) {
      toast.warning('Número da ordem de serviço não encontrado!');
      return;
    }

    setDownloadingReport(true);

    try {
      const response = await serviceOrderService.getServiceOrderReport(serviceOrderNumber);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `laudo_${serviceOrderNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Laudo baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao baixar laudo:', error);
      toast.error(
        error?.response?.data?.message ||
          'Erro ao baixar laudo. Verifique se existe um laudo para esta ordem de serviço.',
      );
    } finally {
      setDownloadingReport(false);
    }
  };

  const downloadBudgetTemplate = async () => {
    const { data, headers } = await budgetService.createTemplate(budget);

    const contentType = headers['content-type'];

    if (contentType?.includes('application/json')) {
      const responseError = JSON.parse(new TextDecoder('utf-8').decode(data));

      if (responseError?.status === 500) {
        throw new Error(responseError?.message);
      }
    }

    const file = new Blob([data], { type: 'application/pdf;charset=utf-8' });
    saveAs(file, `template-${budget.serviceOrder}.pdf`);

    toast.success('Impressão de template realizada com sucesso.');
  };

  const generatePaymentLink = async () => {
    if (!budget.gateway) {
      toast.warning('Selecione um gateway de pagamento antes de gerar a cobrança');
      return;
    }

    if (!budget.paymentType) {
      toast.warning('Selecione um tipo de pagamento antes de gerar a cobrança');
      return;
    }

    if (!paymentLinkValue || paymentLinkValue <= 0) {
      toast.warning('Informe o valor para gerar o link de pagamento');
      return;
    }

    // Calcular o valor total do orçamento para validação
    const totalComponents = (budget.components || []).reduce(
      (acc, comp) => acc + comp.sellPrice * comp.amount,
      0,
    );
    const totalServices = (budget.additionalServices || []).reduce(
      (acc, service) => acc + (service.selected ? service.value : 0),
      0,
    );
    const discountAmount = (totalComponents * (budget.discount || 0)) / 100;
    const subtotalValue = totalComponents + totalServices - discountAmount;

    const isCard = budget.paymentType === 'cartao';
    const budgetTotalValue = isCard ? calculateValueWithCardFee(subtotalValue) : subtotalValue;

    // Validar que o valor do link não seja maior que o total do orçamento
    if (paymentLinkValue > budgetTotalValue) {
      toast.error(
        `O valor do link (R$ ${paymentLinkValue.toFixed(
          2,
        )}) não pode ser maior que o total do orçamento (R$ ${budgetTotalValue.toFixed(2)})`,
      );
      return;
    }

    setGeneratingPayment(true);

    try {
      const response = await budgetService.generatePayment({
        budgetData: {
          id: budget.budget.id,
          serviceOrder: budget.serviceOrder,
          jobOrder: budget.jobOrder,
          totalValue: paymentLinkValue,
          description: `Orçamento OS ${budget.serviceOrder} - ${budget.product || ''}`,
          customer: {
            name: budget.client?.name || 'Cliente',
            email: budget.client?.email || '',
            phone: budget.client?.phoneNumber || '',
            cgc: budget.client?.cgc || '',
            address: {
              zipCode: budget.client?.address?.zipCode || '',
              street: budget.client?.address?.street || '',
              number: budget.client?.address?.number || '',
              district: budget.client?.address?.district || '',
              state: budget.client?.address?.state || '',
              city: budget.client?.address?.city || '',
            },
          },
          paymentType: budget.paymentType,
          installments: budget.installments || 1,
          messages: `Referente a manutenção de aparelho para à service ${budget.serviceOrder}`,
          observation: `Cobrança por boleto realizada à service ${budget.serviceOrder}`,
        },
        gateway: budget.gateway,
      });

      setPaymentLink(response.data);
      toast.success('Link de pagamento gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar link de pagamento:', error);
      toast.error(
        error?.response?.data?.message || 'Erro ao gerar link de pagamento. Tente novamente.',
      );
    } finally {
      setGeneratingPayment(false);
    }
  };

  const value = {
    currentView,
    setCurrentView,
    selectedBudget,
    setSelectedBudget,
    budget,
    loadingBudgetData,
    downloadingReport,
    generatingPayment,
    confirmingBudget,
    paymentLink,
    setPaymentLink,
    paymentLinkValue,
    setPaymentLinkValue,
    finalBudgetValue,
    setFinalBudgetValue,
    budgets,
    budgetsData,
    loadingBudgets,
    refetchBudgets,
    total,
    totalPages,
    budgetFilters,
    updateBudgetFilters,
    gateways,
    validationErrors,
    validateBudget,
    handleSelectBudget,
    handleBackToPending,
    updateComponent,
    updateComponentPrice,
    updateService,
    updateServicePrice,
    updatePaymentType,
    updateGateway,
    updateStatus,
    updateRejectionReason,
    updateDiscount,
    updateInstallments,
    calculateValueWithCardFee,
    confirmBudget,
    sendWhatsAppMessage,
    copyWhatsAppMessage,
    downloadServiceOrderReport,
    downloadBudgetTemplate,
    generatePaymentLink,
  };

  return <BudgetContext.Provider value={value}>{children}</BudgetContext.Provider>;
}

export function useBudget() {
  const context = useContext(BudgetContext);
  if (!context) {
    throw new Error('useBudget must be used within an BudgetProvider');
  }
  return context;
}
